<template>
  <div
    v-click-outside="onClickOutside"
    class="top-nav-button notification"
    :class="classes"
    @click="toggle"
  >
    <Badge color="#f44336">
      <span v-if="!!countBadgeText" slot="badge">{{ countBadgeText }}</span>
      <Icon class="notification__icon">{{ bellMedium }}</Icon>
    </Badge>
    <div v-show="isOpen" class="notification__content" @click.stop="">
      <div class="notification__content__list">
        <NotificationItem
          v-for="(notif, index) in allNotifications"
          :key="index + keyModifier"
          :notif="notif"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Notification menu
 */

// Vuex utils
import { createNamespacedHelpers } from 'vuex';
import { FETCH_NOTIFICATIONS, READ_NOTIFICATIONS } from '@/store/modules/notifications/action-types';
import { GET_NOTIFICATIONS } from '@/store/modules/notifications/mutation-types';
// Components
import Badge from '@/components/badge.vue';
import Icon from '@/components/icon.vue';
import NotificationItem from '@/components/notification-item.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils & assets
import { bellMedium } from '@/assets/icons';


const { mapState } = createNamespacedHelpers('notifications');

export default {

  name: 'NotificationMenu',

  components: {
    Badge,
    Icon,
    NotificationItem,
  },

  mixins: [mixinSocketHandler],

  props: {
    // NOTE: Not used yet
    upcomingMeetings: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      bellMedium,
      isOpen: false,
      /** Last opening of the notifications panel */
      wasOpenAt: null,
      keyModifier: 0,
      updateInterval: null,
    };
  },

  computed: {
    /** Classic notifications and upcoming meetings */
    allNotifications() {
      return (this.upcomingMeetings && this.upcomingMeetings.length)
        ? this.upcomingMeetings.concat(this.notifications)
        : this.notifications;
    },
    classes() {
      return { open: this.isOpen };
    },
    /** Display number of unread notifications even if the panel is closed */
    countBadgeText() {
      const count = this.unreadNotifCount;
      if (count === 0) {
        return '';
      } if (count > 9) {
        return '9+';
      }
      return count.toString();
    },
    unreadNotifCount() {
      let count = 0;
      for (let i = 0, len = this.notifications.length; i < len; i++) {
        if (Number(this.notifications[i].isRead) !== 1) {
          count++;
        }
      }
      return count;
    },
    ...mapState({
      notifications: state => state[GET_NOTIFICATIONS.stateKey] || [],
      notificationsLoading: state => state[GET_NOTIFICATIONS.loadingKey],
    }),
  },

  watch: {
    $route() {
      this.onClickOutside();
    },
    currentTeam(newVal, oldVal) {
      if (!(oldVal && oldVal.id) && newVal.id) {
        this.refresh();
      }
    },
  },

  mounted() {
    this.$moment.relativeTimeThreshold('ss', -1);
    this.updateInterval = setInterval(this.refresh, 60000);
    this.handleSocketMessage(SocketMessages.NOTIF_MSG, this.refresh);
  },

  beforeDestroy() {
    clearInterval(this.updateInterval);
  },

  methods: {
    /** Fetch 20 last notifications */
    refresh() {
      if (this.currentTeam && this.currentTeam.id && !this.notificationsLoading) {
        this.$store.dispatch(`notifications/${FETCH_NOTIFICATIONS}`);
      }
    },
    /** Hide panel when clicking anywhere */
    onClickOutside() {
      if (this.isOpen) {
        this.toggle();
      }
    },
    /** Open or close the panel */
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.wasOpenAt = this.$moment();
        this.keyModifier += this.allNotifications.length;
      } else if (this.$moment().diff(this.wasOpenAt, 'ms') > 500) {
        // After opening and closing, set all notifications as read
        if (this.notifications.some(({ isRead }) => isRead === 0)) {
          this.$store.dispatch(`notifications/${READ_NOTIFICATIONS}`, { ids: this.notifications.map(n => n.id) });
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.notification {

  &.open {
    background: $color-gray-background;
  }

  &__content {
    position: absolute;
    border-radius: 3px;
    right: 0;
    width: 350px;
    max-width: 100%;
    top: $header-height;
    z-index: 10018;
    background-color: #fff;
    border-radius: $main-border-radius;
    cursor: default;
    box-shadow: $light-box-shadow;

    &__list {
      max-height: 300px;
      overflow-x: hidden;
      padding: 0.7rem;
    }
  }

  &__icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .badge__badge {
    width: 1rem;
    height: 1rem;
  }
}

@media (max-width: $smartphone-landscape) {
  .notification {
    &__content {
      top: $smartphone-header-height;
    }
  }
}
</style>
